import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { matchesAll } from "@/lib/trFold";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ShoppingBag, Loader2, Printer, Search, ChevronDown, ChevronUp, ShoppingCart,
  AlertTriangle, Package, ExternalLink, RefreshCcw, TrendingDown,
  Layers, Scissors, Boxes, Plus, Truck, Receipt, ClipboardList, Eye, RotateCcw, FileText, X,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import NewPurchaseDialog from "@/components/NewPurchaseDialog";
import QuickSupplierDialog from "@/components/QuickSupplierDialog";
import PurchaseQuoteDialog from "@/components/PurchaseQuoteDialog";
import BulkQuoteDialog, { vLabel } from "@/components/BulkQuoteDialog";
import BulkPurchaseDialog from "@/components/BulkPurchaseDialog";
import DirectPurchaseDialog from "@/components/DirectPurchaseDialog";
import QuotesBrowserDialog from "@/components/QuotesBrowserDialog";
import CancelPurchaseRequestDialog from "@/components/CancelPurchaseRequestDialog";
import { PurchaseOrderPdfButtons } from "@/components/PurchaseOrderPdfButtons";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 3 });
// ST aşaması backend'de runtime hesaplanır (r.stage); Aktif sıralama: teklifsiz → teklifli/PO'suz → kısmi PO
const STAGE_RANK = { active_none: 0, active_quoted: 1, active_partial: 2 };
const STAGE_UI = {
  active_none: { label: "Teklif / satın alma yok", chip: "border-rose-600 text-rose-300 bg-rose-950/50", bar: "border-l-rose-600" },
  active_quoted: { label: "Teklif var · PO yok", chip: "border-orange-500 text-orange-300 bg-orange-950/40", bar: "border-l-orange-500" },
  active_partial: { label: "Kısmi PO", chip: "border-yellow-500 text-yellow-200 bg-yellow-950/40", bar: "border-l-yellow-500" },
  ordered: { label: "Siparişe bağlı · mal bekleniyor", chip: "border-sky-500 text-sky-300 bg-sky-950/40", bar: "border-l-sky-500" },
  completed: { label: "Mal kabul tamam", chip: "border-emerald-500 text-emerald-300 bg-emerald-950/40", bar: "border-l-emerald-500" },
  cancelled: { label: "İptal edildi", chip: "border-slate-600 text-slate-400 bg-slate-900", bar: "border-l-slate-600" },
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "—");
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "");
// Kompakt tarih izi — yalnız mevcut alanlardan türetilen (boşsa gösterilmez)
function DateTrail({ need, buy, recv, testId }) {
  const parts = [["İhtiyaç", need, "text-slate-300"], ["Alım", buy, "text-sky-300"], ["Mal Kabul", recv, "text-emerald-300"]].filter(([, d]) => d);
  if (!parts.length) return null;
  return (
    <span className="text-[10px] font-mono text-muted-foreground whitespace-nowrap" data-testid={testId}>
      {parts.map(([l, d, c], i) => <span key={l}>{i ? " · " : ""}{l} <b className={c}>{fmtDay(d)}</b></span>)}
    </span>
  );
}

// Hazır ürün satırları varyant bazında ayrışır: product_id::variant_id
const rowKey = (m) => (m?.kind === "hazir_urun" ? `${m.material_id}::${m.variant_id || ""}` : m?.material_id);

export default function PurchaseRequirements() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const canReqEdit = can("purchase_requirements", "edit");  // SEC-003: backend ile birebir
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [view, setView] = useState("materials"); // "orders" | "materials" (default: materials → checkboxes visible)
  const [openId, setOpenId] = useState(null);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [directOpen, setDirectOpen] = useState(false);
  const [quotesBrowserOpen, setQuotesBrowserOpen] = useState(false);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [quoteTarget, setQuoteTarget] = useState(null); // Teklif karşılaştırma hedefi (materials_agg satırı)
  const [bulkReq, setBulkReq] = useState(null); // Toplu teklif — ST objesi
  const [buyReq, setBuyReq] = useState(null); // Toplu satın al — ST objesi
  const [cancelReq, setCancelReq] = useState(null); // ST belge iptali
  const canReqCancel = can("purchases", "edit");
  const [reqStage, setReqStage] = useState("active"); // active | ordered | completed (runtime r.stage)
  const [matStage, setMatStage] = useState("active"); // malzeme ekranı aşaması (runtime m.stage)
  const [requests, setRequests] = useState([]); // Satış teklifinden oluşan satın alma talepleri
  const [reqTab, setReqTab] = useState("hazir"); // hazir: ST talepleri | malzeme: sipariş/BOM eksikleri
  const [openReqId, setOpenReqId] = useState(null);
  const [reqQ, setReqQ] = useState(""); // Hazır Ürün Alımı araması
  const [initialMaterial, setInitialMaterial] = useState(null);
  // Bulk PO create
  const [selected, setSelected] = useState({}); // {material_id: true}
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null); // {created:[{po_no,id,supplier_name}]}
  const [openPoMap, setOpenPoMap] = useState({}); // {material_id: {po_no, id}} — for duplicate protection
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [supplierPickerOpen, setSupplierPickerOpen] = useState(false);
  const [supplierOverrides, setSupplierOverrides] = useState({}); // {material_id: supplier_id}
  const [supplierSearch, setSupplierSearch] = useState("");
  // Requirement cancellation
  const [cancelTarget, setCancelTarget] = useState(null); // {order, row}
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  // Bulk cancel
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);
  const [bulkCancelItems, setBulkCancelItems] = useState([]);
  const [bulkCancelReason, setBulkCancelReason] = useState("");
  const [bulkCancelling, setBulkCancelling] = useState(false);
  // History
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyActiveOnly, setHistoryActiveOnly] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const toggleSelect = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const clearSelection = () => setSelected({});

  const openPurchaseFor = (material_id, kind, variant_id = "", name = "", code = "") => {
    if (kind === "hazir_urun") {
      setInitialMaterial({ type: "product", id: material_id, variant_id, name, code });
      setDirectOpen(true);
      return;
    }
    setInitialMaterial({ type: kind === "kumas" ? "fabric" : "accessory", id: material_id });
    setPurchaseOpen(true);
  };
  const openPurchaseBlank = () => {
    setInitialMaterial(null);
    setPurchaseOpen(true);
  };
  // Talepsiz hazır ürün eksiği → mevcut Satın Alma Talebi altyapısı (stock-replenish, sipariş kaynaklı) → Toplu Teklif
  const [startingKey, setStartingKey] = useState("");
  const startTradedRequest = async (m) => {
    if (startingKey) return;
    setStartingKey(rowKey(m));
    try {
      const { data: rq } = await api.post("/purchase-requests/stock-replenish", {
        product_id: m.material_id, variant_id: m.variant_id || "", order_ids: (m.orders || []).map((o) => o.order_id),
      });
      toast.success(`${rq.already ? "Mevcut" : "Oluşturuldu"}: Satın Alma Talebi ${rq.request_no} — teklif girin, ardından Toplu Satın Al`);
      await load();
      setReqStage("active"); setOpenReqId(rq.id); setBulkReq(rq);
    } catch (e) { toast.error(formatApiError(e)); } finally { setStartingKey(""); }
  };
  const cancelTradedOrder = (m, o) => openCancelDialog(
    { order_id: o.order_id, order_no: o.order_no, customer_name: o.customer_name },
    { kind: "hazir_urun", product_id: m.material_id, variant_id: m.variant_id || "", unit: m.unit, shortage: o.shortage,
      name: `${m.name} | ${m.variant_label || "—"}`, code: m.code },
  );

  const load = async () => {
    setLoading(true);
    try {
      const [reqRes, poRes, supRes, prRes] = await Promise.all([
        api.get("/purchase-requirements"),
        api.get("/purchase-orders").catch(() => ({ data: [] })),
        api.get("/suppliers").catch(() => ({ data: [] })),
        api.get("/purchase-requests").catch(() => ({ data: [] })),
      ]);
      setRequests(prRes.data || []);
      setData(reqRes.data);
      // Build material→openPO map (only ordered/partial_received considered open)
      const map = {};
      const openPos = (poRes.data || []).filter((p) => ["ordered", "partial_received"].includes(p.status));
      for (const po of openPos) {
        for (const it of (po.items || [])) {
          if (it.product_id) {
            const k = `${it.product_id}::${it.variant_id || ""}`;
            if (!map[k]) map[k] = { po_no: po.po_no, id: po.id, supplier_name: po.supplier_name };
            continue;
          }
          const mid = it.material_id;
          if (mid && !map[mid]) map[mid] = { po_no: po.po_no, id: po.id, supplier_name: po.supplier_name };
        }
      }
      setOpenPoMap(map);
      setAllSuppliers(supRes.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filteredOrders = useMemo(() => {
    if (!data) return [];
    if (!q.trim()) return data.orders;
    return data.orders.filter((o) =>
      matchesAll(q, [o.order_no, o.customer_name, ...(o.product_names || [])])
    );
  }, [data, q]);

  const matOnly = useMemo(() => (data?.materials_agg || []).filter((m) => m.kind !== "hazir_urun"), [data]);
  const tradedRows = useMemo(() => (data?.materials_agg || []).filter((m) => m.kind === "hazir_urun")
    .sort((a, b) => (STAGE_RANK[a.stage] ?? 9) - (STAGE_RANK[b.stage] ?? 9) || String(a.oldest_order_at || "").localeCompare(String(b.oldest_order_at || ""))), [data]);
  const filteredMaterials = useMemo(() => {
    if (!data) return [];
    const src = matStage === "completed" ? (data.materials_done || [])
      : matOnly.filter((m) => (matStage === "active" ? String(m.stage || "active").startsWith("active") : m.stage === matStage));
    const rows = !q.trim() ? src : src.filter((m) => matchesAll(q, [m.name, m.code, m.supplier_name]));
    return matStage === "active"
      ? [...rows].sort((a, b) => (STAGE_RANK[a.stage] ?? 9) - (STAGE_RANK[b.stage] ?? 9) || String(a.oldest_order_at || "").localeCompare(String(b.oldest_order_at || "")))
      : rows;
  }, [data, q, matStage, matOnly]);
  const matCounts = useMemo(() => ({
    active: matOnly.filter((m) => String(m.stage || "active").startsWith("active")).length,
    ordered: matOnly.filter((m) => m.stage === "ordered").length,
    completed: (data?.materials_done || []).length,
  }), [data, matOnly]);

  const printPage = () => {
    window.print();
  };

  const selectedMaterials = useMemo(() => {
    if (!data) return [];
    return (data.materials_agg || []).filter((m) => selected[rowKey(m)]);
  }, [data, selected]);

  // Materials whose primary supplier is missing/invalid → user must pick
  const validSupplierIds = useMemo(() => new Set(allSuppliers.map((s) => s.id)), [allSuppliers]);
  const invalidSupplierMaterials = useMemo(() => (
    selectedMaterials.filter((m) => !supplierOverrides[rowKey(m)] && (!m.supplier_id || !validSupplierIds.has(m.supplier_id)))
  ), [selectedMaterials, supplierOverrides, validSupplierIds]);

  const filteredSuppliers = useMemo(() => {
    if (!supplierSearch.trim()) return allSuppliers;
    return allSuppliers.filter((s) => matchesAll(supplierSearch, [s.name, s.contact_person, s.phone, s.email]));
  }, [allSuppliers, supplierSearch]);

  const submitPOs = async () => {
    setCreating(true);
    try {
      const lines = selectedMaterials.map((m) => ({
        kind: m.kind === "kumas" ? "fabric" : (m.kind === "aksesuar" ? "accessory" : "product"),
        supplier_id: supplierOverrides[rowKey(m)] || m.supplier_id,
        ...(m.kind === "hazir_urun"
          ? { product_id: m.material_id, variant_id: m.variant_id || "", variant_sku: m.code || "", product_name: m.name || "" }
          : { material_id: m.material_id, material_code: m.code, material_name: m.name }),
        quantity: Number(m.total_shortage || 0),
        unit: m.unit || "adet",
        unit_price: Number(m.unit_cost || 0),
        currency: "TRY",
        tax_rate: 0,
      }));
      const { data: res } = await api.post("/purchase-orders/bulk-create", { lines });
      toast.success(`${res.count} satın alma siparişi oluşturuldu`);
      setCreateResult(res);
      clearSelection();
      setSupplierOverrides({});
      setSupplierPickerOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setCreating(false); }
  };

  const createPOs = async () => {
    if (selectedMaterials.length === 0) {
      toast.error("Lütfen en az bir satır seçin");
      return;
    }
    // Duplicate protection: block materials already having an open PO
    const dup = selectedMaterials.find((m) => openPoMap[rowKey(m)]);
    if (dup) {
      const info = openPoMap[rowKey(dup)];
      toast.error(`${dup.name} için zaten açık sipariş var: ${info.po_no} (${info.supplier_name || "—"}). Önce onu tamamlayın.`);
      return;
    }
    // If any material lacks a valid supplier, open picker dialog
    const invalid = selectedMaterials.filter((m) => !supplierOverrides[rowKey(m)] && (!m.supplier_id || !validSupplierIds.has(m.supplier_id)));
    if (invalid.length > 0) {
      setSupplierPickerOpen(true);
      return;
    }
    await submitPOs();
  };

  const assignSupplier = (materialId, supplierId) => {
    setSupplierOverrides((s) => ({ ...s, [materialId]: supplierId }));
  };

  const openCancelDialog = (order, row) => {
    setCancelTarget({ order, row });
    setCancelReason("");
  };
  const doCancelRequirement = async () => {
    if (!cancelTarget) return;
    const { order, row } = cancelTarget;
    const materialId = row.fabric_id || row.accessory_id || row.product_id;
    setCancelling(true);
    try {
      await api.post(`/orders/${order.order_id}/cancel-requirement`, {
        material_kind: row.kind,
        material_id: materialId,
        variant_id: row.variant_id || "",
        material_name: row.name,
        cancelled_qty: Number(row.shortage || 0),
        reason: cancelReason.trim(),
      });
      toast.success("İhtiyaç iptal edildi");
      setCancelTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setCancelling(false); }
  };

  const doBulkCancel = async () => {
    const items = bulkCancelItems.filter((it) => it.selected && it.cancelled_qty > 0);
    if (items.length === 0) { toast.error("En az bir satır seçin"); return; }
    setBulkCancelling(true);
    try {
      const { data } = await api.post("/orders/bulk-cancel-requirement", {
        reason: bulkCancelReason.trim(),
        items: items.map((it) => ({
          order_id: it.order_id, material_kind: it.material_kind,
          material_id: it.material_id, material_name: it.material_name,
          cancelled_qty: Number(it.cancelled_qty),
        })),
      });
      if (data.created_count > 0) toast.success(`${data.created_count} ihtiyaç iptal edildi`);
      if (data.skipped_count > 0) toast.warning(`${data.skipped_count} satır atlandı (açık satın alma siparişi veya hata)`);
      setBulkCancelOpen(false);
      clearSelection();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBulkCancelling(false); }
  };

  const loadHistory = async (activeOverride) => {
    setHistoryLoading(true);
    try {
      const activeOnly = activeOverride ?? historyActiveOnly;
      const { data } = await api.get("/purchase-requirements/cancellations", {
        params: activeOnly ? { only_active: true } : {},
      });
      setHistoryRows(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setHistoryLoading(false); }
  };

  const restoreCancellation = async (id) => {
    setRestoringId(id);
    try {
      await api.post(`/orders/cancelled-requirements/${id}/restore`);
      toast.success("İptal geri alındı");
      loadHistory();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setRestoringId(null); }
  };

  const isRestoreAllowed = (row) => {
    if (row.restored_at) return false;
    if (!row.cancelled_at) return false;
    try {
      const age = (Date.now() - new Date(row.cancelled_at).getTime()) / 1000;
      return age <= 24 * 3600;
    } catch { return false; }
  };

  return (
    <div>
      <PageHeader
        title="Satın Alma İhtiyaçları"
        subtitle="Aktif siparişlerdeki eksik kumaş & aksesuar toplamları"
        breadcrumbs={["Ana Sayfa", "Satın Alma İhtiyaçları"]}
        actions={
          <div className="grid grid-cols-2 gap-2 sm:flex print:hidden">
            <Button
              data-testid="req-quotes-browser-btn"
              variant="outline"
              onClick={() => setQuotesBrowserOpen(true)}
              className="w-full border-sky-800 text-sky-300 hover:bg-sky-950/40 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors"
            >
              <FileText className="w-4 h-4 mr-2" /> Tedarikçi Teklifleri
            </Button>
            <Button disabled={!(can("purchases", "add"))} title={!(can("purchases", "add")) ? "Satın alma siparişi oluşturma yetkiniz yok (purchases.add)" : undefined}
              data-testid="direct-purchase-btn"
              onClick={() => setDirectOpen(true)}
              className="w-full bg-emerald-600 hover:bg-emerald-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" /> Direkt Satın Alma
            </Button>
            <Button
              data-testid="req-new-supplier-btn"
              onClick={() => setSupplierOpen(true)}
              variant="outline"
              className="w-full border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm uppercase tracking-wider text-xs font-semibold"
            >
              <Truck className="w-4 h-4 mr-2" /> Yeni Tedarikçi
            </Button>
            <Button
              data-testid="req-new-purchase-btn"
              onClick={openPurchaseBlank}
              className="w-full bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors"
            >
              <Receipt className="w-4 h-4 mr-2" /> Manuel / Faturalı Alım
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-5">
        {/* Üst sekmeler: Hazır Ürün (ST talepleri) / Stok-Üretim Malzemesi */}
        <div className="flex gap-1 border-b border-border" data-testid="req-tabs">
          <button
            data-testid="req-tab-hazir"
            onClick={() => setReqTab("hazir")}
            className={`px-4 py-2 text-xs uppercase tracking-wider font-mono border-b-2 transition-colors ${
              reqTab === "hazir" ? "border-amber-500 text-amber-400" : "border-transparent text-muted-foreground hover:text-slate-200"
            }`}
          >
            Hazır Ürün Alımı{requests.length ? ` (${requests.length})` : ""}
          </button>
          <button
            data-testid="req-tab-malzeme"
            onClick={() => setReqTab("malzeme")}
            className={`px-4 py-2 text-xs uppercase tracking-wider font-mono border-b-2 transition-colors ${
              reqTab === "malzeme" ? "border-blue-500 text-blue-400" : "border-transparent text-muted-foreground hover:text-slate-200"
            }`}
          >
            Stok / Üretim Malzemesi Alımı
          </button>
        </div>

        {/* Hazır Ürün Alımı — satış tekliflerinden oluşan ST talepleri (collapsed) */}
        {!data && (loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-sm text-muted-foreground" data-testid="req-initial-loading">
            <Loader2 className="w-7 h-7 text-primary animate-spin" />
            Satın alma ihtiyaçları yükleniyor…
          </div>
        ) : (
          <Card className="rounded-sm border-rose-900 bg-rose-950/30 p-6 text-center text-sm" data-testid="req-load-error">
            <div className="text-rose-300 font-semibold mb-2">Satın alma ihtiyaçları yüklenemedi.</div>
            <Button size="sm" variant="outline" className="rounded-sm" onClick={load} data-testid="req-load-retry">Tekrar dene</Button>
          </Card>
        ))}

        {data && reqTab === "hazir" && (() => {
          const q = reqQ.trim().toLowerCase();
          const inStage = (r) => (reqStage === "active" ? String(r.stage || "active").startsWith("active") : r.stage === reqStage);
          const activeCount = requests.filter((r) => String(r.stage || "active").startsWith("active")).length + tradedRows.length;
          const stageCount = (s) => requests.filter((r) => r.stage === s).length;
          const filteredReqs = requests.filter(inStage).filter((r) => !q || matchesAll(q, [
            r.request_no, r.source_sales_quote_no, ...(r.items || []).flatMap((it) => [it.name, it.code]),
          ])).sort((a, b) => reqStage === "active"
            ? (STAGE_RANK[a.stage] ?? 9) - (STAGE_RANK[b.stage] ?? 9) || String(a.created_at || "").localeCompare(String(b.created_at || ""))
            : String(b.created_at || "").localeCompare(String(a.created_at || "")));
          const liveReqs = requests.filter((r) => r.stage !== "cancelled");
          const totalItems = liveReqs.reduce((a, r) => a + (r.items || []).length, 0);
          const totalQuotes = liveReqs.reduce((a, r) => a + (r.quotes_count || 0), 0);
          return (
          <div className="space-y-3" data-testid="purchase-requests-section">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="requests-summary">
              <SummaryCard icon={ShoppingCart} color="text-rose-500" label="Satın Alma Bekleyen" value={activeCount} testId="req-summary-requests" />
              <SummaryCard icon={Layers} color="text-sky-500" label="Toplam Ürün / Kalem" value={totalItems} testId="req-summary-items" />
              <SummaryCard icon={FileText} color="text-emerald-500" label="Toplam Teklif" value={totalQuotes} testId="req-summary-quotes" />
            </div>
            <div className="flex flex-wrap gap-1" data-testid="req-stage-tabs">
              {[["active", "Aktif İhtiyaçlar", activeCount, "border-rose-600 text-rose-300"], ["ordered", "Siparişe Bağlananlar", stageCount("ordered"), "border-sky-600 text-sky-300"],
                ["completed", "Tamamlananlar", stageCount("completed"), "border-emerald-600 text-emerald-300"],
                ["cancelled", "İptal Edilenler", stageCount("cancelled"), "border-slate-500 text-slate-300"]].map(([k, l, n, c]) => (
                <button key={k} onClick={() => setReqStage(k)} data-testid={`req-stage-${k}`}
                  className={`px-3 h-8 rounded-sm border text-[11px] uppercase font-mono transition-colors ${reqStage === k ? `${c} bg-slate-900` : "border-border text-muted-foreground hover:text-slate-200"}`}>
                  {l} <span className="ml-1 font-bold" data-testid={`req-stage-count-${k}`}>{n}</span>
                </button>
              ))}
            </div>
            <div className="relative" data-testid="requests-search-wrap">
              <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="requests-search"
                placeholder="ST no, teklif no, ürün adı veya kodu ara…"
                value={reqQ}
                onChange={(e) => setReqQ(e.target.value)}
                className="pl-9 rounded-sm"
              />
            </div>
            {reqStage === "active" && tradedRows.length > 0 && (
              <div className="space-y-2" data-testid="traded-shortages-section">
                <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">
                  Siparişlerden gelen hazır ürün eksikleri (Satın Alma Talebi açılmamış) · {tradedRows.length}
                </div>
                <MaterialsView materials={tradedRows} openId={openId} setOpenId={setOpenId}
                  onBuy={(mid, kind, vid) => startTradedRequest(tradedRows.find((x) => x.material_id === mid && (x.variant_id || "") === vid))}
                  onQuotes={startTradedRequest} startingKey={startingKey} traded
                  onCancelOrder={canReqEdit ? cancelTradedOrder : undefined}
                  openPoMap={openPoMap} selectable={false} />
              </div>
            )}

            {filteredReqs.length === 0 ? (reqStage === "active" && tradedRows.length > 0 ? null : (
              <Card className="rounded-sm border-dashed p-10 text-center" data-testid="requests-empty">
                <ShoppingCart className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <div className="font-bold mb-1">{q ? "Eşleşen talep yok" : "Açık satın alma talebi yok"}</div>
                <div className="text-sm text-muted-foreground">{q ? "Arama kriterini değiştirin." : 'Teklif detayından "Satın Alma Talebi Oluştur" ile talep açabilirsiniz.'}</div>
              </Card>
            )) : filteredReqs.map((r) => {
              const items = r.items || [];
              const byUnit = {};
              items.forEach((it) => { const u = it.unit || "adet"; byUnit[u] = (byUnit[u] || 0) + Number(it.quantity || 0); });
              const totalTxt = Object.entries(byUnit).map(([u, q]) => `${fmtQty(q)} ${u}`).join(" + ");
              const variantCount = new Set(items.map((it) => it.variant_id || "")).size || items.length;
              const names = [...new Set(items.map((it) => it.name).filter(Boolean))];
              const open = openReqId === r.id;
              return (
                <div key={r.id} className={`border border-border border-l-4 ${(STAGE_UI[r.stage] || STAGE_UI.active_none).bar} rounded-sm`} data-testid={`purchase-request-card-${r.id}`}>
                  <div
                    role="button"
                    tabIndex={0}
                    data-testid={`purchase-request-toggle-${r.id}`}
                    onClick={() => setOpenReqId(open ? null : r.id)}
                    onKeyDown={(e) => { if (e.key === "Enter") setOpenReqId(open ? null : r.id); }}
                    className="w-full flex flex-wrap items-center gap-x-2 gap-y-0.5 px-3 py-2 text-left hover:bg-muted/20 transition-colors cursor-pointer"
                  >
                    <span className="text-amber-400 font-mono text-xs whitespace-nowrap"><span className="text-muted-foreground font-sans">Satın Alma Talebi </span>{r.request_no}</span>
                    <span className={`text-[9px] uppercase font-mono border rounded-sm px-1.5 py-0.5 whitespace-nowrap ${(STAGE_UI[r.stage] || STAGE_UI.active_none).chip}`} data-testid={`purchase-request-stage-${r.id}`}>
                      {(STAGE_UI[r.stage] || STAGE_UI.active_none).label}{r.stage === "active_partial" ? ` · Kalan ${fmtQty(r.remaining_total)}` : ""}
                    </span>
                    <span className="text-muted-foreground text-xs font-mono whitespace-nowrap hidden sm:inline">{r.source_sales_quote_no}</span>
                    <span className="text-xs truncate flex-1">{names[0] || "—"}{names.length > 1 ? ` +${names.length - 1}` : ""}</span>
                    <span className="text-xs font-mono whitespace-nowrap">{totalTxt}</span>
                    <DateTrail need={r.created_at} buy={r.purchase_date} recv={r.receipt_date} testId={`purchase-request-dates-${r.id}`} />
                    {(r.po_linked_total || 0) > 0 && (
                      <span className="text-[10px] font-mono whitespace-nowrap" data-testid={`purchase-request-linked-${r.id}`}>
                        <span className="text-sky-400">Bağlanan {fmtQty(r.po_linked_total)}</span> · <span className="text-amber-400">Kalan {fmtQty(r.remaining_total)}</span>
                      </span>
                    )}
                    <span className="text-[10px] font-mono text-muted-foreground whitespace-nowrap hidden md:inline">{variantCount} varyant</span>
                    <Badge variant="outline" title={`${r.quote_lines_count || 0} varyant satırı`} className="text-[9px] uppercase font-mono whitespace-nowrap" data-testid={`purchase-request-quotes-count-${r.id}`}>{r.quotes_count || 0} teklif</Badge>
                    {r.stage !== "cancelled" && <Button
                      size="sm"
                      className="rounded-sm h-7 text-[10px] uppercase font-mono bg-emerald-600 hover:bg-emerald-500 text-white"
                      data-testid={`purchase-request-bulk-quote-${r.id}`}
                      onClick={(e) => { e.stopPropagation(); setOpenReqId(r.id); setBulkReq(r); }}
                    >
                      <FileText className="w-3 h-3 mr-1" /> Toplu Teklif Topla{items.length > 1 ? ` (${items.length})` : ""}
                    </Button>}
                    {r.stage !== "cancelled" && (r.quotes_count || 0) > 0 && (r.remaining_total ?? 1) > 0 && (
                      <Button
                        size="sm"
                        className="rounded-sm h-7 text-[10px] uppercase font-mono bg-sky-600 hover:bg-sky-500 text-white"
                        data-testid={`purchase-request-bulk-buy-${r.id}`}
                        onClick={(e) => { e.stopPropagation(); setOpenReqId(r.id); setBuyReq(r); }}
                      >
                        <ShoppingBag className="w-3 h-3 mr-1" /> Toplu Satın Al
                      </Button>
                    )}
                    {canReqCancel && String(r.stage || "active").startsWith("active") && (
                      <Button size="sm" variant="ghost" title="Satın Alma Talebini İptal Et"
                        className="rounded-sm h-7 text-[10px] uppercase font-mono text-rose-400 hover:text-rose-300 hover:bg-rose-950/30"
                        data-testid={`purchase-request-cancel-${r.id}`}
                        onClick={(e) => { e.stopPropagation(); setCancelReq(r); }}>
                        <XCircle className="w-3 h-3 mr-1" /> İptal Et
                      </Button>
                    )}
                    {open ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
                  </div>
                  {open && (
                    <div className="border-t border-border">
                      <div className="px-3 py-1.5 text-[10px] font-mono text-muted-foreground">
                        {r.customer_name} · {new Date(r.created_at).toLocaleDateString("tr-TR")} · {r.created_by}
                      </div>
                      {r.stage === "cancelled" && (
                        <div className="px-3 pb-1.5 text-[11px] font-mono text-rose-300" data-testid={`purchase-request-cancel-info-${r.id}`}>
                          İptal: {fmtDate(r.cancelled_at)} · {r.cancelled_by} · Gerekçe: {r.cancel_reason}
                        </div>
                      )}
                      {(r.linked_pos || []).length > 0 && (
                        <div className="px-3 pb-1.5 flex flex-wrap items-center gap-2" data-testid={`purchase-request-pos-${r.id}`}>
                          <span className="text-[10px] uppercase font-mono text-muted-foreground">Satın Alma Siparişi:</span>
                          {r.linked_pos.map((po) => <PurchaseOrderPdfButtons key={po.id} po={po} />)}
                        </div>
                      )}
                      <table className="w-full text-sm">
                        <tbody className="divide-y divide-border">
                          {items.map((it, i) => (
                            <tr key={i} data-testid={`purchase-request-item-${r.id}-${i}`}>
                              <td className="px-3 py-1.5 text-xs">{it.code || "—"}{vLabel(it) ? <span className="text-sky-400"> · {vLabel(it)}</span> : null}<span className="text-muted-foreground"> · İhtiyaç {fmtQty(it.quantity)} {it.unit}</span><span className="font-mono" data-testid={`request-item-linked-${r.id}-${i}`}> | <span className="text-sky-400">PO'ya Bağlanan {fmtQty(it.po_linked_qty || 0)}</span> | <span className={(it.quantity - (it.po_linked_qty || 0)) > 0 ? "text-amber-400" : "text-emerald-400"}>Kalan {fmtQty(Math.max(0, it.quantity - (it.po_linked_qty || 0)))}</span></span>{it.name ? <span className="text-muted-foreground hidden sm:inline"> — {it.name}</span> : ""}</td>
                              <td className="px-3 py-1.5 text-xs font-mono whitespace-nowrap">{fmtQty(it.quantity)} {it.unit}</td>
                              <td className="px-3 py-1.5 text-right">
                                <Button
                                  size="sm" variant="ghost"
                                  className="rounded-sm h-6 text-[10px] font-mono text-slate-400 hover:text-sky-300"
                                  data-testid={`request-item-quotes-${r.id}-${i}`}
                                  onClick={() => setQuoteTarget({
                                    material_id: it.material_id, variant_id: it.variant_id || "", kind: it.kind,
                                    name: it.name, unit: it.unit, total_shortage: it.quantity, manual: true,
                                    request_id: r.id, source_sales_quote_no: r.source_sales_quote_no,
                                  })}
                                >
                                  <FileText className="w-3 h-3 mr-1" /> Tekil teklif / geçmiş
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          );
        })()}

        {/* Stok / Üretim Malzemesi Alımı — mevcut sipariş/BOM eksikleri (değişmedi) */}
        {data && reqTab === "malzeme" && (<>
        {/* KPI summary */}
        {data && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="req-summary">
            <SummaryCard
              icon={ShoppingCart} color="text-rose-500"
              label="Satın Alma Bekleyen"
              value={matCounts.active}
              testId="req-summary-pending"
            />
            <SummaryCard
              icon={ShoppingBag} color="text-rose-500"
              label="Eksik Malzemeli Sipariş"
              value={data.summary.order_count}
              testId="req-summary-orders"
            />
            <SummaryCard
              icon={Layers} color="text-amber-500"
              label="Farklı Malzeme Türü"
              value={matOnly.length}
              testId="req-summary-materials"
            />
            <SummaryCard
              icon={TrendingDown} color="text-blue-500"
              label="Tahmini Alım Tutarı (₺)"
              value={`₺${fmt(matOnly.reduce((a, m) => a + Number(m.estimated_cost || 0), 0))}`}
              testId="req-summary-cost"
            />
          </div>
        )}

        {/* Toolbar */}
        <Card className="rounded-sm bg-card border-border p-3 flex items-center gap-3 flex-wrap print:hidden">
          <div className="relative flex-1 min-w-64">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="req-search"
              placeholder={view === "orders" ? "Sipariş no, müşteri veya ürün ara…" : "Malzeme adı, kod veya tedarikçi ara…"}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 rounded-sm"
            />
          </div>
          <Select value={view} onValueChange={setView}>
            <SelectTrigger data-testid="req-view-switch" className="w-52 rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="orders">Siparişe Göre</SelectItem>
              <SelectItem value="materials">Malzemeye Göre</SelectItem>
            </SelectContent>
          </Select>
          <Button
            data-testid="req-refresh"
            variant="outline" size="sm"
            onClick={load}
            className="rounded-sm"
          >
            <RefreshCcw className="w-4 h-4 mr-1" /> Yenile
          </Button>
          <Button
            data-testid="req-history-btn"
            onClick={() => { setHistoryOpen(true); loadHistory(); }}
            variant="outline"
            className="rounded-sm"
          >
            <FileText className="w-4 h-4 mr-1" /> İptal Geçmişi
          </Button>
          {canReqEdit && <Button
            data-testid="req-cancel-top"
            onClick={() => {
              if (selectedMaterials.length === 0) return;
              if (selectedMaterials.length === 1) {
                // Single-selection legacy path (only 1 order case)
                const m = selectedMaterials[0];
                const orders = m.orders || [];
                if (orders.length === 0) { toast.error("Bu malzeme için aktif sipariş bulunamadı."); return; }
                if (orders.length === 1) {
                  const src = orders[0];
                  const row = {
                    name: m.name, code: m.code, unit: m.unit, shortage: src.shortage, kind: m.kind,
                    fabric_id: m.kind === "kumas" ? m.material_id : undefined,
                    accessory_id: m.kind === "aksesuar" ? m.material_id : undefined,
                  };
                  openCancelDialog({ order_id: src.order_id, order_no: src.order_no, customer_name: src.customer_name }, row);
                  return;
                }
              }
              // Multi-material OR single with multiple orders → bulk dialog
              const items = [];
              for (const m of selectedMaterials) {
                for (const src of (m.orders || [])) {
                  items.push({
                    key: `${src.order_id}::${m.material_id}`,
                    order_id: src.order_id,
                    order_no: src.order_no,
                    customer_name: src.customer_name,
                    material_kind: m.kind,
                    material_id: m.material_id,
                    material_name: m.name,
                    material_code: m.code,
                    unit: m.unit,
                    cancelled_qty: Number(src.shortage || 0),
                    selected: true,
                  });
                }
              }
              setBulkCancelItems(items);
              setBulkCancelReason("");
              setBulkCancelOpen(true);
            }}
            disabled={selectedMaterials.length === 0 || view !== "materials"}
            variant="outline"
            className="rounded-sm border-rose-800 text-rose-400 hover:bg-rose-950/40 disabled:opacity-40"
          >
            <AlertTriangle className="w-4 h-4 mr-1" />
            İhtiyacı İptal Et
            {selectedMaterials.length > 1 && (
              <span className="ml-1.5 rounded-sm bg-rose-900/60 px-1.5 text-xs">{selectedMaterials.length}</span>
            )}
          </Button>}
          <Button
            data-testid="req-create-pos"
            onClick={createPOs}
            disabled={(creating || selectedMaterials.length === 0 || view !== "materials") || !(can("purchases", "add"))} title={!(can("purchases", "add")) ? "Satın alma siparişi oluşturma yetkiniz yok (purchases.add)" : undefined}
            className="rounded-sm bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40"
          >
            {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ClipboardList className="w-4 h-4 mr-1" />}
            Satın Alma Siparişi Oluştur
            {selectedMaterials.length > 0 && (
              <span className="ml-1.5 rounded-sm bg-emerald-800/60 px-1.5 text-xs">
                {selectedMaterials.length}
              </span>
            )}
          </Button>
          <Button
            data-testid="req-print"
            onClick={printPage}
            className="rounded-sm bg-primary hover:bg-primary/90"
          >
            <Printer className="w-4 h-4 mr-1" /> Yazdır
          </Button>
        </Card>

        {view === "materials" && (
          <div className="flex flex-wrap gap-1 print:hidden" data-testid="mat-stage-tabs">
            {[["active", "Aktif İhtiyaçlar", "border-rose-600 text-rose-300"], ["ordered", "Siparişe Bağlananlar", "border-sky-600 text-sky-300"],
              ["completed", "Tamamlananlar", "border-emerald-600 text-emerald-300"]].map(([k, l, c]) => (
              <button key={k} onClick={() => setMatStage(k)} data-testid={`mat-stage-${k}`}
                className={`px-3 h-8 rounded-sm border text-[11px] uppercase font-mono transition-colors ${matStage === k ? `${c} bg-slate-900` : "border-border text-muted-foreground hover:text-slate-200"}`}>
                {l} <span className="ml-1 font-bold" data-testid={`mat-stage-count-${k}`}>{matCounts[k]}</span>
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
          </div>
        ) : !data || (view === "orders" && filteredOrders.length === 0) || (view === "materials" && filteredMaterials.length === 0) ? (
          <Card className="rounded-sm border-dashed p-10 text-center" data-testid="req-empty">
            <ShoppingBag className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <div className="font-bold mb-1">Eksik malzeme yok</div>
            <div className="text-sm text-muted-foreground">
              Şu an aktif siparişlerin malzeme ihtiyaçları için yeterli stok var.
            </div>
          </Card>
        ) : view === "orders" ? (
          <OrdersView orders={filteredOrders} openId={openId} setOpenId={setOpenId} navigate={navigate} onBuy={openPurchaseFor} onCancel={canReqEdit ? openCancelDialog : undefined} />
        ) : (
          <MaterialsView
            materials={filteredMaterials}
            openId={openId}
            setOpenId={setOpenId}
            onBuy={openPurchaseFor}
            selected={selected}
            onToggleSelect={toggleSelect}
            openPoMap={openPoMap}
            onQuotes={(m) => setQuoteTarget(m)}
          />
        )}
        </>)}
      </div>

      {/* Print header */}
      <div className="hidden print:block print-title p-6">
        <h1 className="text-2xl font-bold">Satın Alma İhtiyaçları — Fucan Personel Giyim</h1>
        <div className="text-sm text-slate-500">Rapor Tarihi: {new Date().toLocaleString("tr-TR")}</div>
      </div>

      <QuotesBrowserDialog
        open={quotesBrowserOpen}
        onOpenChange={setQuotesBrowserOpen}
        onPick={(m) => setQuoteTarget(m)}
      />
      <DirectPurchaseDialog
        open={directOpen}
        onOpenChange={setDirectOpen}
        onSuccess={load}
        onQuoteRequest={(m) => setQuoteTarget(m)}
        initialProduct={initialMaterial?.type === "product" ? initialMaterial : null}
      />
      <NewPurchaseDialog
        open={purchaseOpen}
        onOpenChange={setPurchaseOpen}
        initialMaterial={initialMaterial}
        onSuccess={() => { setPurchaseOpen(false); load(); }}
      />
      <QuickSupplierDialog
        open={supplierOpen}
        onOpenChange={setSupplierOpen}
        onCreated={() => setSupplierOpen(false)}
      />
      <PurchaseQuoteDialog
        open={!!quoteTarget}
        onOpenChange={(v) => { if (!v) setQuoteTarget(null); }}
        material={quoteTarget}
        suppliers={allSuppliers}
        onChanged={load}
      />
      <BulkQuoteDialog
        request={bulkReq}
        onClose={() => setBulkReq(null)}
        onSaved={load}
      />
      <BulkPurchaseDialog request={buyReq} onClose={() => setBuyReq(null)} onSaved={load} />
      <CancelPurchaseRequestDialog request={cancelReq} onClose={() => setCancelReq(null)} onDone={load} />

      {/* Missing/invalid supplier picker */}
      <Dialog open={supplierPickerOpen} onOpenChange={(v) => { if (!v) setSupplierPickerOpen(false); }}>
        <DialogContent className="max-w-2xl" data-testid="supplier-picker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Tedarikçi Seçimi Gerekli
            </DialogTitle>
            <DialogDescription>
              Aşağıdaki malzemelerin tedarikçisi mevcut değil veya geçersiz. Her satır için geçerli bir tedarikçi seçin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Input
              placeholder="Tedarikçi ara: ad, telefon, e-posta..."
              value={supplierSearch}
              onChange={(e) => setSupplierSearch(e.target.value)}
              data-testid="supplier-picker-search"
            />
          </div>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {invalidSupplierMaterials.map((m) => {
              const chosen = supplierOverrides[rowKey(m)];
              return (
                <div key={rowKey(m)} className="border rounded-sm p-2" data-testid={`sp-row-${rowKey(m)}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{m.name}</div>
                      <div className="text-xs text-muted-foreground">{m.code} · {fmtQty(m.total_shortage)} {m.unit}</div>
                    </div>
                    {chosen && (
                      <span className="text-xs text-emerald-400 flex-none ml-2">
                        ✓ {allSuppliers.find((s) => s.id === chosen)?.name || chosen}
                      </span>
                    )}
                  </div>
                  <div className="max-h-32 overflow-y-auto border rounded-sm">
                    {filteredSuppliers.length === 0 && (
                      <div className="text-xs text-muted-foreground p-3 text-center">Tedarikçi bulunamadı</div>
                    )}
                    {filteredSuppliers.slice(0, 20).map((s) => (
                      <button
                        key={s.id}
                        className={`w-full text-left px-2 py-1 text-sm hover:bg-muted/60 ${chosen === s.id ? "bg-emerald-500/10" : ""}`}
                        onClick={() => assignSupplier(rowKey(m), s.id)}
                        data-testid={`sp-pick-${rowKey(m)}-${s.id}`}
                      >
                        {s.name} <span className="text-xs text-muted-foreground">{s.phone || ""}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSupplierPickerOpen(false); setSupplierOverrides({}); }}>Vazgeç</Button>
            <Button
              onClick={submitPOs}
              disabled={creating || invalidSupplierMaterials.length > 0}
              className="bg-emerald-600 hover:bg-emerald-500"
              data-testid="sp-confirm"
            >
              {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {invalidSupplierMaterials.length > 0
                ? `${invalidSupplierMaterials.length} malzeme için tedarikçi bekleniyor`
                : "Onayla ve Sipariş Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk requirement cancel */}
      <Dialog open={bulkCancelOpen} onOpenChange={(v) => { if (!v) setBulkCancelOpen(false); }}>
        <DialogContent className="max-w-3xl" data-testid="req-bulk-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-400" /> Toplu İhtiyaç İptali
            </DialogTitle>
            <DialogDescription>
              Aşağıdaki satırlar iptal edilecek. Ortak neden yazabilirsiniz. Açık satın alma siparişi olan satırlar otomatik atlanır.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Ortak İptal Nedeni (opsiyonel)</Label>
            <Textarea rows={2} value={bulkCancelReason} onChange={(e) => setBulkCancelReason(e.target.value)} data-testid="bulk-cancel-reason" />
          </div>
          <div className="border rounded-sm max-h-[50vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase font-mono">
                <tr>
                  <th className="p-2 w-8"></th>
                  <th className="p-2 text-left">Sipariş / Müşteri</th>
                  <th className="p-2 text-left">Malzeme</th>
                  <th className="p-2 text-right">Miktar</th>
                </tr>
              </thead>
              <tbody>
                {bulkCancelItems.map((it, idx) => (
                  <tr key={it.key} className="border-t" data-testid={`bulk-row-${idx}`}>
                    <td className="p-2">
                      <Checkbox
                        checked={it.selected}
                        onCheckedChange={(v) => setBulkCancelItems((rows) => rows.map((r) => r.key === it.key ? { ...r, selected: !!v } : r))}
                      />
                    </td>
                    <td className="p-2">
                      <div className="font-medium">{it.order_no}</div>
                      <div className="text-xs text-muted-foreground">{it.customer_name}</div>
                    </td>
                    <td className="p-2">
                      <div className="font-medium">{it.material_name}</div>
                      <div className="text-xs text-muted-foreground">{it.material_code || "—"}</div>
                    </td>
                    <td className="p-2 text-right">
                      <Input
                        type="number" min="0" step="0.001"
                        className="h-7 text-right w-24 ml-auto"
                        value={it.cancelled_qty}
                        onChange={(e) => setBulkCancelItems((rows) => rows.map((r) => r.key === it.key ? { ...r, cancelled_qty: e.target.value } : r))}
                      />
                      <span className="text-xs text-muted-foreground ml-1">{it.unit}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkCancelOpen(false)}>Vazgeç</Button>
            <Button
              onClick={doBulkCancel}
              disabled={bulkCancelling || bulkCancelItems.filter((it) => it.selected && it.cancelled_qty > 0).length === 0}
              className="bg-rose-600 hover:bg-rose-500"
              data-testid="bulk-cancel-confirm"
            >
              {bulkCancelling && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Seçilenleri İptal Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancellation history */}
      <Dialog open={historyOpen} onOpenChange={(v) => { if (!v) setHistoryOpen(false); }}>
        <DialogContent className="max-w-4xl" data-testid="req-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" /> İptal Geçmişi
            </DialogTitle>
            <DialogDescription>
              Tüm siparişler için iptal edilen ihtiyaçlar. 24 saat içinde geri alınabilir.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={historyActiveOnly}
                onChange={(e) => { setHistoryActiveOnly(e.target.checked); loadHistory(e.target.checked); }}
                data-testid="history-active-only"
              />
              Sadece aktif iptaller
            </label>
            <Button size="sm" variant="ghost" onClick={loadHistory} data-testid="history-refresh">
              <RefreshCcw className={`w-4 h-4 mr-1 ${historyLoading ? "animate-spin" : ""}`} /> Yenile
            </Button>
            <span className="text-xs text-muted-foreground ml-auto">{historyRows.length} kayıt</span>
          </div>
          <div className="border rounded-sm max-h-[55vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 sticky top-0 text-xs uppercase font-mono">
                <tr>
                  <th className="p-2 text-left">Tarih</th>
                  <th className="p-2 text-left">Sipariş</th>
                  <th className="p-2 text-left">Malzeme</th>
                  <th className="p-2 text-right">Miktar</th>
                  <th className="p-2 text-left">Neden</th>
                  <th className="p-2 text-left">İptal Eden</th>
                  <th className="p-2 text-left">Durum</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {historyRows.length === 0 && (
                  <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Kayıt yok</td></tr>
                )}
                {historyRows.map((h) => {
                  const restored = !!h.restored_at;
                  const canRestore = isRestoreAllowed(h);
                  return (
                    <tr key={h.id} className="border-t" data-testid={`history-row-${h.id}`}>
                      <td className="p-2 whitespace-nowrap text-xs">{new Date(h.cancelled_at).toLocaleString("tr-TR")}</td>
                      <td className="p-2">
                        <div className="font-medium">{h.order_no}</div>
                        <div className="text-xs text-muted-foreground">{h.customer_name}</div>
                      </td>
                      <td className="p-2">{h.material_name || "—"}</td>
                      <td className="p-2 text-right font-mono">{h.cancelled_qty}</td>
                      <td className="p-2 text-xs">{h.reason || "—"}</td>
                      <td className="p-2 text-xs">{h.cancelled_by || "—"}</td>
                      <td className="p-2">
                        {restored ? (
                          <span className="text-xs text-emerald-400">Geri Alındı</span>
                        ) : canRestore ? (
                          <span className="text-xs text-amber-400">Aktif · Geri alınabilir</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Aktif · Süresi doldu</span>
                        )}
                      </td>
                      <td className="p-2">
                        {!restored && canRestore && canReqEdit && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={restoringId === h.id}
                            onClick={() => restoreCancellation(h.id)}
                            data-testid={`history-restore-${h.id}`}
                            className="h-7"
                          >
                            {restoringId === h.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
                            Geri Al
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Requirement cancel confirm */}
      <Dialog open={!!cancelTarget} onOpenChange={(v) => { if (!v) setCancelTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="req-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-400" /> İhtiyacı İptal Et
            </DialogTitle>
            <DialogDescription>
              Bu satın alma ihtiyacını iptal etmek istediğinize emin misiniz?
              Müşteri satış siparişi ve üretim siparişi bu işlemden ETKİLENMEZ.
            </DialogDescription>
          </DialogHeader>
          {cancelTarget && (
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-muted-foreground">Sipariş No:</span> <b>{cancelTarget.order.order_no}</b></div>
                <div><span className="text-muted-foreground">Müşteri:</span> <b>{cancelTarget.order.customer_name}</b></div>
                <div className="col-span-2"><span className="text-muted-foreground">Malzeme:</span> <b>{cancelTarget.row.name}</b> ({cancelTarget.row.code || "—"})</div>
                <div className="col-span-2"><span className="text-muted-foreground">İptal Edilecek Miktar:</span> <b className="text-rose-400">{fmtQty(cancelTarget.row.shortage)} {cancelTarget.row.unit}</b></div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs mb-1">İptal Nedeni (opsiyonel)</div>
                <Textarea rows={2} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="req-cancel-reason" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)}>Vazgeç</Button>
            <Button
              onClick={doCancelRequirement}
              disabled={cancelling}
              className="bg-rose-600 hover:bg-rose-500"
              data-testid="req-cancel-confirm"
            >
              {cancelling && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              İhtiyacı İptal Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk PO create result */}
      <Dialog open={!!createResult} onOpenChange={(v) => { if (!v) setCreateResult(null); }}>
        <DialogContent className="max-w-lg" data-testid="req-pos-created-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-emerald-400" />
              Siparişler Oluşturuldu
            </DialogTitle>
            <DialogDescription>
              {createResult?.count} tedarikçi için {createResult?.count} satın alma siparişi hazırlandı.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {(createResult?.created || []).map((po) => (
              <div key={po.id} className="flex items-center justify-between border rounded-sm p-2 gap-2" data-testid={`req-pos-row-${po.id}`}>
                <div className="min-w-0">
                  <div className="font-mono font-semibold">{po.po_no}</div>
                  <div className="text-xs text-muted-foreground truncate">{po.supplier_name || "—"} · {(po.items || []).length} satır · ₺{fmt(po.total_price)}</div>
                </div>
                <div className="flex gap-1 flex-none">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(`/satinalma/po/${po.id}/yazdir`, "_blank", "noopener,noreferrer")}
                    data-testid={`req-pos-print-${po.id}`}
                  >
                    <Printer className="w-3.5 h-3.5 mr-1" /> Yazdır
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate("/mal-kabul")}
                    data-testid={`req-pos-view-${po.id}`}
                  >
                    <Eye className="w-3.5 h-3.5 mr-1" /> Görüntüle
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateResult(null)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ icon: Icon, color, label, value, testId }) {
  return (
    <Card className="rounded-sm bg-card border-border p-4" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-sm bg-muted flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">{label}</div>
          <div className="text-2xl font-bold font-mono">{value}</div>
        </div>
      </div>
    </Card>
  );
}

// ---- Orders view ----------------------------------------------------------
const PRIORITY_META = {
  dusuk: { label: "Düşük", cls: "bg-slate-500/10 text-slate-500 border-slate-500/40" },
  normal: { label: "Normal", cls: "bg-blue-500/10 text-blue-500 border-blue-500/40" },
  yuksek: { label: "Yüksek", cls: "bg-blue-500/10 text-blue-500 border-blue-500/40" },
  acil: { label: "ACİL", cls: "bg-rose-500/15 text-rose-500 border-rose-500/40" },
};

function OrdersView({ orders, openId, setOpenId, navigate, onBuy, onCancel }) {
  return (
    <div className="space-y-2" data-testid="req-orders-view">
      {orders.map((o) => {
        const isOpen = openId === o.order_id;
        const pr = PRIORITY_META[o.priority] || PRIORITY_META.normal;
        return (
          <Card key={o.order_id} className="rounded-sm bg-card border-border overflow-hidden" data-testid={`req-order-${o.order_id}`}>
            <button
              onClick={() => setOpenId(isOpen ? null : o.order_id)}
              className="w-full grid grid-cols-[24px_auto_1fr_100px_120px_120px_140px] gap-3 items-center px-4 py-3 hover:bg-muted/40 transition-colors text-left"
              data-testid={`req-order-${o.order_id}-toggle`}
            >
              {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              <div className="font-mono font-bold text-primary">{o.order_no}</div>
              <div className="min-w-0">
                <div className="font-semibold truncate">{o.customer_name}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {(o.product_names || []).join(", ") || "—"}
                </div>
              </div>
              <div className="text-center">
                <div className="text-[9px] uppercase font-mono text-muted-foreground">Adet</div>
                <div className="font-mono font-bold">{o.quantity}</div>
              </div>
              <div className="text-center">
                <div className="text-[9px] uppercase font-mono text-muted-foreground">Termin</div>
                <div className="font-mono text-xs">{fmtDate(o.due_date)}</div>
              </div>
              <span className={`text-center text-[10px] uppercase font-mono font-bold border rounded-sm py-1 px-2 ${pr.cls}`}>{pr.label}</span>
              <div className="text-right">
                <div className="text-[9px] uppercase font-mono text-muted-foreground">Eksik Tutar</div>
                <div className="font-mono font-bold text-rose-500">₺{fmt(o.total_shortage_cost)}</div>
              </div>
            </button>

            {isOpen && (
              <div className="border-t border-border p-4 bg-muted/20" data-testid={`req-order-${o.order_id}-detail`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase font-mono text-muted-foreground">
                    Eksik Malzemeler ({o.shortage_lines.length})
                  </div>
                  <div className="flex items-center gap-1.5 print:hidden">
                    <Button
                      variant="outline" size="sm"
                      className="rounded-sm h-7 text-xs"
                      onClick={(e) => { e.stopPropagation(); navigate(`/uretim`); }}
                    >
                      <ExternalLink className="w-3 h-3 mr-1" /> Üretim Takibinde Aç
                    </Button>
                  </div>
                </div>
                <div className="border border-border rounded-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 border-b border-border">
                      <tr className="text-[10px] uppercase font-mono text-muted-foreground">
                        <th className="text-left px-3 py-2">Tür</th>
                        <th className="text-left px-3 py-2">Malzeme</th>
                        <th className="text-right px-3 py-2">Gerekli</th>
                        <th className="text-right px-3 py-2">Mevcut</th>
                        <th className="text-right px-3 py-2">Eksik</th>
                        <th className="text-left px-3 py-2">Tedarikçi</th>
                        <th className="text-right px-3 py-2">Tahmini Tutar</th>
                        <th className="text-right px-3 py-2 print:hidden">Alım</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {o.shortage_lines.map((r, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 text-xs">
                            {r.kind === "kumas" ? <span className="inline-flex items-center gap-1 text-blue-500"><Scissors className="w-3 h-3" /> Kumaş</span>
                              : r.kind === "hazir_urun" ? <span className="inline-flex items-center gap-1 text-emerald-500"><Package className="w-3 h-3" /> Hazır Ürün</span>
                              : <span className="inline-flex items-center gap-1 text-amber-500"><Boxes className="w-3 h-3" /> Aksesuar</span>}
                          </td>
                          <td className="px-3 py-2">
                            <div className="font-medium">{r.name}</div>
                            <div className="text-[10px] font-mono text-muted-foreground">{r.code}</div>
                          </td>
                          <td className="px-3 py-2 text-right font-mono">{fmtQty(r.required)} {r.unit}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmtQty(r.available)} {r.unit}</td>
                          <td className="px-3 py-2 text-right font-mono font-bold text-rose-500">
                            -{fmtQty(r.shortage)} {r.unit}
                          </td>
                          <td className="px-3 py-2 text-xs">{r.primary_supplier_name || "—"}</td>
                          <td className="px-3 py-2 text-right font-mono text-rose-500">₺{fmt(r.shortage_cost)}</td>
                          <td className="px-3 py-2 text-right print:hidden">
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-sm h-7 text-[10px] uppercase font-mono border-emerald-800 text-emerald-400 hover:bg-emerald-950/40"
                                data-testid={`req-buy-${r.fabric_id || r.accessory_id || r.product_id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onBuy?.(r.fabric_id || r.accessory_id || r.product_id, r.kind, r.variant_id || "", r.name, r.code);
                                }}
                              >
                                <Plus className="w-3 h-3 mr-1" /> Alım Yap
                              </Button>
                              {onCancel && <Button
                                size="sm"
                                variant="outline"
                                className="rounded-sm h-7 text-[10px] uppercase font-mono border-rose-800 text-rose-400 hover:bg-rose-950/40"
                                data-testid={`req-cancel-${o.order_id}-${r.fabric_id || r.accessory_id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCancel?.(o, r);
                                }}
                              >
                                İhtiyacı İptal Et
                              </Button>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// ---- Materials view -------------------------------------------------------
function MaterialsView({ materials, openId, setOpenId, onBuy, selected, onToggleSelect, openPoMap = {}, onQuotes, selectable = true, traded = false, startingKey = "", onCancelOrder }) {
  return (
    <div className="space-y-2" data-testid="req-materials-view">
      {materials.map((m) => {
        const rk = rowKey(m);
        const isOpen = openId === rk;
        const isChecked = !!selected?.[rk];
        const openPo = openPoMap[rk];
        const noSupplier = !m.supplier_id;
        const disabled = !!openPo;
        return (
          <Card key={rk} className={`rounded-sm bg-card border-border overflow-hidden ${m.stage ? `border-l-4 ${(STAGE_UI[m.stage] || STAGE_UI.active_none).bar}` : ""}`} data-testid={`req-material-${rk}`}>
            <div className="w-full grid grid-cols-[30px_24px_60px_1fr_120px_140px_140px] gap-3 items-center px-4 py-3 hover:bg-muted/40 transition-colors text-left">
              <div className="print:hidden" onClick={(e) => e.stopPropagation()}>
                {selectable && <Checkbox
                  checked={isChecked}
                  onCheckedChange={() => onToggleSelect?.(rk)}
                  disabled={disabled}
                  data-testid={`req-select-${rk}`}
                  aria-label={disabled ? "Zaten sipariş verilmiş" : "Seç"}
                />}
              </div>
              <button
                onClick={() => setOpenId(isOpen ? null : rk)}
                className="contents"
                data-testid={`req-material-${rk}-toggle`}
              >
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {m.kind === "kumas" ? (
                  <span className="inline-flex items-center justify-center gap-1 text-blue-500 text-xs uppercase font-mono border border-blue-500/40 rounded-sm py-1 px-2">
                    <Scissors className="w-3 h-3" /> Kumaş
                  </span>
                ) : m.kind === "hazir_urun" ? (
                  <span className="inline-flex items-center justify-center gap-1 text-emerald-500 text-xs uppercase font-mono border border-emerald-500/40 rounded-sm py-1 px-2">
                    <Package className="w-3 h-3" /> Hazır Ürün
                  </span>
                ) : (
                  <span className="inline-flex items-center justify-center gap-1 text-amber-500 text-xs uppercase font-mono border border-amber-500/40 rounded-sm py-1 px-2">
                    <Boxes className="w-3 h-3" /> Aksesuar
                  </span>
                )}
                <div className="min-w-0">
                  <div className="font-semibold truncate flex items-center gap-2">
                    {m.name}
                    {m.kind === "hazir_urun" && (
                      <span className="text-xs font-mono text-sky-300 border border-sky-800 rounded-sm px-1.5" data-testid={`req-material-size-${rk}`}>{vLabel(m) || "—"}</span>
                    )}
                    {m.stage && (
                      <span className={`text-[9px] uppercase font-mono border rounded-sm px-1.5 py-0.5 whitespace-nowrap ${(STAGE_UI[m.stage] || STAGE_UI.active_none).chip}`} data-testid={`req-material-stage-${rk}`}>
                        {(STAGE_UI[m.stage] || STAGE_UI.active_none).label}{m.stage === "active_partial" ? ` · Kalan ${fmtQty(m.remaining_qty)} ${m.unit}` : ""}
                      </span>
                    )}
                    {m.incoming_qty > 0 && <span className="text-[10px] font-mono text-sky-400 whitespace-nowrap" data-testid={`req-material-incoming-${rk}`}>Yolda {fmtQty(m.incoming_qty)}</span>}
                    {openPo && (
                      <span className="text-[9px] uppercase font-mono border border-sky-500/50 text-sky-300 rounded-sm px-1.5 py-0.5">
                        Sipariş Verildi · {openPo.po_no}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground truncate">
                    {m.code} · Tedarikçi: {noSupplier ? (
                      <span className="text-amber-500">Atanmamış</span>
                    ) : (m.supplier_name || "—")}
                    {m.oldest_order_at ? " · " : ""}<DateTrail need={m.oldest_order_at} buy={m.purchase_date} recv={m.receipt_date} testId={`req-material-dates-${rk}`} />
                    {traded && <span className="whitespace-nowrap" data-testid={`req-material-qty-${rk}`}> · İhtiyaç <b>{fmtQty(m.total_shortage)}</b> | PO'ya Bağlanan <b>0</b> | Kalan <b className="text-amber-400">{fmtQty(m.total_shortage)}</b></span>}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-[9px] uppercase font-mono text-muted-foreground">Sipariş</div>
                  <div className="font-mono font-bold">{m.orders.length}</div>
                </div>
                <div className="text-right">
                  <div className="text-[9px] uppercase font-mono text-muted-foreground">Toplam Eksik</div>
                  <div className="font-mono font-bold text-rose-500">{fmtQty(m.total_shortage)} {m.unit}</div>
                </div>
                <div className="text-right">
                  <div className="text-[9px] uppercase font-mono text-muted-foreground">Tahmini Tutar</div>
                  <div className="font-mono font-bold text-rose-500">₺{fmt(m.estimated_cost)}</div>
                </div>
              </button>
            </div>
            {isOpen && (
              <div className="border-t border-border p-4 bg-muted/20" data-testid={`req-material-${rk}-detail`}>
                <div className="flex items-center justify-between mb-2 print:hidden">
                  <div className="text-xs uppercase font-mono text-muted-foreground">
                    Siparişlere Göre Dağılım
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-sm h-7 text-[10px] uppercase font-mono border-sky-800 text-sky-400 hover:bg-sky-950/40"
                      data-testid={`req-material-quotes-${rk}`}
                      disabled={!!startingKey}
                      onClick={(e) => { e.stopPropagation(); onQuotes?.(m); }}
                    >
                      <FileText className="w-3 h-3 mr-1" /> {traded ? "Satın Alma Talebi Aç + Teklif Topla" : "Teklif Topla / Teklifler"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-sm h-7 text-[10px] uppercase font-mono border-emerald-800 text-emerald-400 hover:bg-emerald-950/40"
                      data-testid={`req-material-buy-${rk}`}
                      disabled={!!startingKey}
                      onClick={(e) => { e.stopPropagation(); onBuy?.(m.material_id, m.kind, m.variant_id || "", m.name, m.code); }}
                    >
                      <Plus className="w-3 h-3 mr-1" /> {traded ? "Satın Alma Başlat" : "Bu Malzemeye Alım Yap"}
                    </Button>
                  </div>
                </div>
                <div className="border border-border rounded-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 border-b border-border">
                      <tr className="text-[10px] uppercase font-mono text-muted-foreground">
                        <th className="text-left px-3 py-2">Sipariş</th>
                        <th className="text-left px-3 py-2">Müşteri</th>
                        <th className="text-right px-3 py-2">Bu Siparişteki Eksik</th>
                        {onCancelOrder && <th className="px-3 py-2" />}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {m.orders.map((o, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 font-mono font-bold text-primary">{o.order_no}</td>
                          <td className="px-3 py-2">{o.customer_name}</td>
                          <td className="px-3 py-2 text-right font-mono text-rose-500">
                            -{fmtQty(o.shortage)} {m.unit}
                          </td>
                          {onCancelOrder && (
                            <td className="px-3 py-2 text-right">
                              <Button size="sm" variant="ghost" className="h-6 rounded-sm text-[10px] text-rose-400 hover:text-rose-300"
                                data-testid={`req-material-cancel-${rk}-${o.order_id}`} onClick={() => onCancelOrder(m, o)}>
                                <XCircle className="w-3 h-3 mr-1" /> İhtiyacı İptal Et
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
